import { CompareSegmentsGroup } from '@/components/quick-study-results/CompareSegmentsPicker'
import { parseCutId } from '@/components/quick-study-results/divergingStacked'
import { segPriority } from '@/components/quick-study-results/audience-segments'

/** Template / run-input key. Same string as python `GenerationSpec.segment_by`. */
export const TWIN_TEST_SEGMENT_BY_INPUT_KEY = 'segment_by'

export const TWIN_TEST_CUT_SUGGESTION_LIMIT = 3

export interface TwinTestCutSuggestion {
  key: string
  label: string
}

/**
 * Map picker cut ids (`attribute::value`) to the python `segment_by` attribute key.
 * First selected cut wins; empty selection is absent.
 */
export function segmentByFromSelectedCuts(selected: Array<string>): string | null {
  for (const cutId of selected) {
    const attribute = parseCutId(cutId).attribute.trim()
    if (attribute) {
      return attribute
    }
  }
  return null
}

/**
 * Rank 1–3 likely `segment_by` axes from picker groups.
 * Only attributes that already vary (2+ valued options with n>0) qualify.
 * No LLM: balance across values, then the same demographic priority
 * `buildSegmentCandidates` uses.
 */
export function suggestTwinTestCuts(
  groups: Array<CompareSegmentsGroup>,
): Array<TwinTestCutSuggestion> {
  const ranked: Array<TwinTestCutSuggestion & { score: number; priority: number }> = []

  for (const group of groups) {
    const key = segmentByFromSelectedCuts(
      group.options.map((option) => option.id),
    )
    if (!key) {
      continue
    }
    const varying = group.options.filter((option) => option.n == null || option.n > 0)
    if (varying.length < 2) {
      continue
    }
    ranked.push({
      key,
      label: group.groupLabel.trim() || key,
      score: cutVarianceScore(varying.map((option) => option.n ?? 1)),
      priority: segPriority(key),
    })
  }

  return ranked
    .sort((a, b) => {
      if (b.score !== a.score) {
        return b.score - a.score
      }
      if (a.priority !== b.priority) {
        return a.priority - b.priority
      }
      return a.key.localeCompare(b.key)
    })
    .slice(0, TWIN_TEST_CUT_SUGGESTION_LIMIT)
    .map(({ key, label }) => ({ key, label }))
}

/** Human label for the active `segment_by` attribute, or null when none. */
export function activeCutLabel(
  groups: Array<CompareSegmentsGroup>,
  segmentBy: string | null | undefined,
): string | null {
  const key = segmentBy?.trim() ?? ''
  if (!key) {
    return null
  }
  const match = groups.find((group) =>
    group.options.some((option) => parseCutId(option.id).attribute.trim() === key),
  )
  const label = match?.groupLabel.trim()
  return label || key
}

/** Cut ids on `groups` that belong to one `segment_by` attribute. */
export function cutsForSegmentBy(
  groups: Array<CompareSegmentsGroup>,
  segmentBy: string,
): Array<string> {
  const key = segmentBy.trim()
  if (!key) {
    return []
  }
  return groups.flatMap((group) =>
    group.options
      .filter((option) => parseCutId(option.id).attribute.trim() === key)
      .map((option) => option.id),
  )
}

function cutVarianceScore(counts: Array<number>): number {
  const total = counts.reduce((sum, n) => sum + n, 0)
  if (total <= 0 || counts.length < 2) {
    return 0
  }
  const herfindahl = counts.reduce((sum, n) => {
    const share = n / total
    return sum + share * share
  }, 0)
  return (1 - herfindahl) * total
}

/** kebab-case option test id from a cut id (`gender::Male` → `gender-male`). */
export function twinTestCutOptionTestId(cutId: string): string {
  const slug = cutId
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return `twin-test-cut-option-${slug}`
}
